import { useEffect, useMemo, useRef, useState } from 'react';
import API from '../../utils/API';
import Swal from 'sweetalert2';


const HistoricalDownload = ({ channelId, channelName, disabled }) => {

    // Keep these two in sync with the backend's MIN_START_DATE / MAX_MONTHS_SPAN.
    const MIN_MONTH = '2021-01';
    const MAX_MONTHS_SPAN = 6;

    const pad2 = (n) => String(n).padStart(2, '0');

    const toMonthString = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

    // Inclusive count of calendar months between two "YYYY-MM" strings.
    const monthsBetween = (start, end) => {
        const [sy, sm] = start.split('-').map(Number);
        const [ey, em] = end.split('-').map(Number);
        return (ey - sy) * 12 + (em - sm) + 1;
    };

    // "YYYY-MM" -> {startDate: "YYYY-MM-01", endDate: "YYYY-MM-<last day>"}
    const monthBounds = (monthStr) => {
        const [y, m] = monthStr.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this one
        return {
            startDate: `${monthStr}-01`,
            endDate: `${monthStr}-${pad2(lastDay)}`,
        };
    };

    const CURRENT_MONTH = toMonthString(new Date());

    const readBlobAsText = (blob) =>
        new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsText(blob);
        });

    const showDownloadError = async (err) => {
        let message = 'An error occurred, please try again later.';

        if (err.response && err.response.data instanceof Blob) {
            const text = await readBlobAsText(err.response.data);
            if (text) {
                try {
                    const parsed = JSON.parse(text);
                    message = parsed.error || text;
                } catch (e) {
                    message = text;
                }
            }
        } else if (err.response && err.response.data && err.response.data.error) {
            message = err.response.data.error;
        }

        Swal.fire({
            title: 'Error.',
            text: message,
            icon: 'warning',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#007b4e',
        });
    };

    const [startMonth, setStartMonth] = useState(CURRENT_MONTH);
    const [endMonth, setEndMonth] = useState(CURRENT_MONTH);
    const [downloading, setDownloading] = useState(false);
    const cancelRef = useRef(null);

    // Cancel an in-flight download if the component unmounts (e.g. the user
    // navigates away or picks a different channel mid-request).
    useEffect(() => {
        return () => {
            if (cancelRef.current) cancelRef.current.cancel('cancelling');
        };
    }, []);

    const rangeError = useMemo(() => {
        if (!startMonth || !endMonth) return 'Select a start and end month.';
        if (endMonth < startMonth) return 'End month must be on or after the start month.';
        if (monthsBetween(startMonth, endMonth) > MAX_MONTHS_SPAN) {
            return `Range can't exceed ${MAX_MONTHS_SPAN} months.`;
        }
        return null;
    }, [startMonth, endMonth]);

    const handleDownload = () => {
        if (!channelId || downloading || rangeError) return;

        setDownloading(true);
        const cancelToken = API.cancelToken();
        cancelRef.current = cancelToken;

        const { startDate } = monthBounds(startMonth);
        const { endDate } = monthBounds(endMonth);

        // NOTE: param order/shape changed from (source, year, channel_id) to
        // (source, start, end, channel_id) - update api.js's getHistorical to
        // match, e.g.:
        //   getHistorical: function (source, start, end, channel_id) {
        //       return apiInstance.get(
        //           `${MICROSERVICE_URL}/historical?start=${start}&end=${end}&channel_id=${channel_id}`,
        //           { withCredentials: true, cancelToken: source.token, responseType: 'blob' }
        //       );
        //   },
        API.getHistorical(cancelToken, startDate, endDate, channelId).then((res) => {
            setDownloading(false);

            // Pull the filename the server sent (Content-Disposition), fall
            // back to a sensible default if it's missing for any reason.
            const disposition = res.headers && res.headers['content-disposition'];
            let filename = `historical_${channelName}_${startDate}_to_${endDate}.zip`;
            if (disposition) {
                const match = disposition.match(/filename="?([^"]+)"?/i);
                if (match && match[1]) filename = match[1];
            }

            const url = window.URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }).catch((err) => {
            setDownloading(false);
            if (err.message === 'cancelling') return;
            showDownloadError(err);
        });
    };

    return (
        <div className='w-full flex flex-wrap items-center'>
            <label className='laptop:w-4/12 mobile:w-1/2 tablet:w-2/5 text-base input-label-color' htmlFor="historical_start_month">
                Download Historical Data
            </label>
            <input
                className='p-1 laptop:w-2/12 tablet:w-4/12 mobile:w-1/2 mr-2 text-base input-color p-2 outline-none hide-input-background'
                type="month"
                name="historical_start_month"
                min={MIN_MONTH}
                max={endMonth || CURRENT_MONTH}
                value={startMonth}
                disabled={disabled || downloading}
                onChange={(e) => setStartMonth(e.target.value)} />
            <span className='mr-2 input-label-color'>to</span>
            <input
                className='p-1 laptop:w-2/12 tablet:w-4/12 mobile:w-1/2 mr-2 text-base input-color p-2 outline-none hide-input-background'
                type="month"
                name="historical_end_month"
                min={startMonth || MIN_MONTH}
                max={CURRENT_MONTH}
                value={endMonth}
                disabled={disabled || downloading}
                onChange={(e) => setEndMonth(e.target.value)} />
            <button
                type="button"
                className='px-4 py-2 mr-2 text-base rounded text-white disabled:opacity-50 disabled:cursor-not-allowed'
                style={{ backgroundColor: '#007b4e' }}
                disabled={disabled || downloading || !channelId || !!rangeError}
                onClick={handleDownload}>
                {downloading ? 'Preparing…' : 'Download CSV (.zip)'}
            </button>
            {rangeError &&
                <p className='text-red text-xs w-full'>{rangeError}</p>
            }
        </div>
    );
};

export default HistoricalDownload;
